import { type EpisodeData } from '../api/goApi';

/**
 * Calculates free episodes based on dynamic pricing rules
 */
export const getFreeEpisodesByDynamicPrice = (episodes: EpisodeData[]): number | null => {
  const freeEpisodesDynamicPricing = episodes
    ?.filter(
      (episode) =>
        episode.season === 1 &&
        episode?.dynamic_price?.wait_time === 0 &&
        !episode?.dynamic_price?.paid_only &&
        !episode?.dynamic_price?.subscribers_only,
    )
    ?.sort((episode, nextEpisode) => episode.number - nextEpisode.number);

  // return null if freeEpisodesDynamicPricing is undefined or first sorted episode not equal 1. In this case we would use free_episode_count.
  if (!freeEpisodesDynamicPricing || freeEpisodesDynamicPricing[0]?.number !== 1) {
    return null;
  }

  // We should find first free episode by dynamic pricing start with 1 episode.
  const firstFreeDPEpisode: EpisodeData[] = [];
  freeEpisodesDynamicPricing?.forEach((episode, index) => {
    if (firstFreeDPEpisode.length === 0) {
      firstFreeDPEpisode.push(episode);
    } else if (episode.number - 1 === firstFreeDPEpisode[index - 1]?.number) {
      firstFreeDPEpisode.push(episode);
    }
  });

  return firstFreeDPEpisode?.length || 0;
};

/**
 * Determines the number of free chapters, prioritizing dynamic pricing over static free_episodes_count
 */
export const getStoryFreeChapters = (freeEpisodesCount: number, episodes?: EpisodeData[]): number => {
  // Default to the static free episodes count
  const defaultFreeChapters = freeEpisodesCount || 0;

  if (!episodes || episodes.length === 0) {
    return defaultFreeChapters;
  }

  // Try to get free chapters from dynamic pricing
  const dynamicFreeChapters = getFreeEpisodesByDynamicPrice(episodes);

  // If dynamic pricing provides a valid count, use it; otherwise fall back to static count
  return dynamicFreeChapters !== null ? dynamicFreeChapters : defaultFreeChapters;
};

/**
 * Enhanced episode availability check that prioritizes dynamic pricing over static free chapters
 */
export const isEpisodeAvailable = (
  episodeId: number,
  maxUnlockedEpisodeId: number,
  freeEpisodesCount: number,
  episodes?: EpisodeData[],
): boolean => {
  // Always allow access to already unlocked episodes
  if (episodeId <= maxUnlockedEpisodeId) {
    return true;
  }

  // Use dynamic pricing-aware free chapters calculation
  const actualFreeChapters = getStoryFreeChapters(freeEpisodesCount, episodes);

  return episodeId <= actualFreeChapters;
};
